import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { Participant } from '@/types/evaluation.types';
import { ParticipantType, InvitationStatus } from '@/types/evaluation.types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail, Phone, Calendar, Clock, Trash2, Edit, Send, AlertCircle, CheckCircle2 } from 'lucide-react';
import { canResendInvitation, getDaysUntilExpiration, isInvitationExpired } from '@/services/participants/invitationService';

interface ParticipantCardProps {
  participant: Participant;
  isPrimaryHost: boolean;
  onRemove: (id: string) => void;
  onEdit: (id: string) => void;
  onResendInvitation: (id: string) => void;
  onRoleChange?: (id: string, newRole: ParticipantType) => void;
  onCriteriaGroupChange?: (id: string, groupIds: string[] | null) => void;
}

/**
 * Card displaying participant information with actions
 */
export function ParticipantCard({
  participant,
  isPrimaryHost,
  onRemove,
  onEdit,
  onResendInvitation,
}: ParticipantCardProps) {
  const { t } = useTranslation(['participants', 'common']);

  // Role badge variant mapping
  const getRoleBadgeVariant = (role: ParticipantType): 'default' | 'secondary' | 'outline' => {
    switch (role) {
      case ParticipantType.Host:
        return 'default'; // Primary color
      case ParticipantType.Evaluator:
        return 'secondary';
      case ParticipantType.Viewer:
        return 'outline';
      default:
        return 'secondary';
    }
  };

  // Invitation status badge variant
  const getInvitationBadgeVariant = (status: InvitationStatus): 'default' | 'secondary' | 'outline' => {
    switch (status) {
      case InvitationStatus.Accepted:
        return 'default';
      case InvitationStatus.Pending:
        return 'secondary';
      case InvitationStatus.Declined:
        return 'outline';
      default:
        return 'secondary';
    }
  };

  // Calculate expiration status
  const daysRemaining = getDaysUntilExpiration(participant);
  const expired = isInvitationExpired(participant);

  return (
    <Card data-testid={`participant-card-${participant.id}`} className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Header: Name and Badges */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold truncate">{participant.name}</h3>
              <div className="flex flex-wrap gap-2 mt-2">
                <Badge variant={getRoleBadgeVariant(participant.participantType)}>
                  {t(`participants:types.${participant.participantType.toLowerCase()}`)}
                </Badge>
                <Badge variant={getInvitationBadgeVariant(participant.invitationStatus)}>
                  {t(`participants:invitationStatus.${participant.invitationStatus.toLowerCase()}`)}
                </Badge>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-1">
              {/* Resend invitation button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onResendInvitation(participant.id)}
                aria-label={t('participants:actions.resendInvitation', { name: participant.name })}
                disabled={!canResendInvitation(participant)}
                title={
                  !canResendInvitation(participant)
                    ? t('participants:actions.resendDisabledTooltip')
                    : t('participants:actions.resendInvitation', { name: participant.name })
                }
              >
                <Send className="h-4 w-4" aria-hidden="true" />
              </Button>

              {/* Edit button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(participant.id)}
                aria-label={t('participants:actions.edit', { name: participant.name })}
              >
                <Edit className="h-4 w-4" aria-hidden="true" />
              </Button>

              {/* Remove button (disabled for primary host) */}
              {!isPrimaryHost && (
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid={`remove-participant-${participant.id}`}
                  onClick={() => onRemove(participant.id)}
                  aria-label={t('participants:actions.remove', { name: participant.name })}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-2 text-sm">
            {participant.email && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="truncate">{participant.email}</span>
              </div>
            )}
            {participant.phone && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{participant.phone}</span>
              </div>
            )}
          </div>

          {/* Metadata: Invited and Last Activity */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              <span>
                {t('participants:invitedDate')}: {format(participant.invitedDate, 'MMM d, yyyy')}
              </span>
            </div>
            {participant.lastActivityDate && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                <span>
                  {t('participants:lastActivity')}: {format(participant.lastActivityDate, 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </div>

          {/* Invitation Expiration Status */}
          {daysRemaining !== null && (
            <div className="text-xs">
              {expired ? (
                <div className="flex items-center gap-1.5 text-destructive">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{t('participants:invitationExpired')}</span>
                </div>
              ) : participant.invitationStatus === InvitationStatus.Pending && (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>
                    {daysRemaining === 0
                      ? t('participants:expiresToday')
                      : daysRemaining === 1
                      ? t('participants:expiresTomorrow')
                      : t('participants:expiresInDays', { count: daysRemaining })}
                  </span>
                </div>
              )}
              {participant.invitationStatus === InvitationStatus.Accepted && (
                <div className="flex items-center gap-1.5 text-green-600">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{t('participants:invitationAccepted')}</span>
                </div>
              )}
            </div>
          )}

          {/* Criteria Group Assignment Info */}
          {participant.participantType === ParticipantType.Evaluator && (
            <div className="pt-2 border-t text-sm">
              {participant.criteriaGroupAssignments === null ? (
                <p className="text-muted-foreground">
                  {t('participants:criteriaAccess.allGroups')}
                </p>
              ) : participant.criteriaGroupAssignments.length === 0 ? (
                <p className="text-muted-foreground">
                  {t('participants:criteriaAccess.noGroups')}
                </p>
              ) : (
                <p className="text-muted-foreground">
                  {t('participants:criteriaAccess.specificGroups', {
                    count: participant.criteriaGroupAssignments.length,
                  })}
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
